import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * CONTACTS PAGE - PRODUCTION VERSION
 *
 * This file uses real API calls to fetch and manage contacts.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ContactsTable, ContactsFilterModal } from "./components";
import { Pagination } from "@/components/common/Pagination";
import { PageHeader, useToast } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { contactsService } from "@/api/services/contacts.service";
import { getRoutePrefix, getHomeRoute } from "@/utils/navigation";
import { Role } from "@/types/auth.types";
import styles from "./Contacts.module.css";
export const Contacts = () => {
    const navigate = useNavigate();
    const { user, switchToClientView } = useAuth();
    const { error: showError } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const isMountedRef = useRef(true);
    // Track the latest request to ignore stale responses
    const latestRequestIdRef = useRef(0);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    // Parse URL: prefer CompanyType/ContactType params, fallback to filter=key=value or legacy param names
    const filterParam = searchParams.get("filter");
    const companyTypeFromUrl = searchParams.get("CompanyType") ||
        (filterParam?.startsWith("companyType=")
            ? decodeURIComponent(filterParam.slice("companyType=".length))
            : "") ||
        searchParams.get("companyType") ||
        "";
    const contactTypeFromUrl = searchParams.get("ContactType") ||
        (filterParam?.startsWith("contactType=")
            ? decodeURIComponent(filterParam.slice("contactType=".length))
            : "") ||
        searchParams.get("contactType") ||
        "";
    const tierFromUrl = searchParams.get("Tier") ||
        (filterParam?.startsWith("tier=")
            ? decodeURIComponent(filterParam.slice("tier=".length))
            : "") ||
        searchParams.get("tier") ||
        "";
    const otherFilterFromUrl = filterParam &&
        !filterParam.startsWith("companyType=") &&
        !filterParam.startsWith("contactType=") &&
        !filterParam.startsWith("tier=")
        ? filterParam
        : "all";
    const activeFilterFromUrl = companyTypeFromUrl || contactTypeFromUrl
        ? companyTypeFromUrl
            ? "companyType"
            : "contactType"
        : otherFilterFromUrl;
    // Filter state
    const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
    const [activeFilter, setActiveFilter] = useState(activeFilterFromUrl);
    const [companyTypeFilter, setCompanyTypeFilter] = useState(companyTypeFromUrl || "");
    const [contactTypeFilter, setContactTypeFilter] = useState(contactTypeFromUrl || "");
    const [tierFilter, setTierFilter] = useState(tierFromUrl || "");
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [filterModalType, setFilterModalType] = useState(null);
    const fetchContacts = useCallback(async () => {
        // Increment request ID to track the latest request
        const requestId = ++latestRequestIdRef.current;
        try {
            if (!isMountedRef.current)
                return;
            setLoading(true);
            setError(null);
            const filters = {
                search: searchTerm || undefined,
                filter: activeFilter !== "all" ? activeFilter : undefined,
                companyType: companyTypeFilter || undefined,
                contactType: contactTypeFilter || undefined,
                tier: tierFilter || undefined,
                page: currentPage,
            };
            const response = await contactsService.getContacts(filters);
            // Check if component is still mounted AND this is still the latest request
            // This prevents stale responses from overwriting newer results
            if (!isMountedRef.current || requestId !== latestRequestIdRef.current) {
                return;
            }
            const { data, pagination } = response.data;
            setContacts(data);
            setCurrentPage(pagination.pageNumber);
            setTotalPages(pagination.totalPages);
            setPageSize(pagination.pageSize);
        }
        catch (err) {
            // Ignore errors if component unmounted, request was cancelled, or this is a stale request
            if (!isMountedRef.current || requestId !== latestRequestIdRef.current) {
                return;
            }
            // Ignore cancellation errors (they're expected when component unmounts)
            if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
                return;
            }
            setError("Failed to load contacts");
            console.error("Error fetching contacts:", err);
        }
        finally {
            // Only update loading state if this is still the latest request
            if (isMountedRef.current && requestId === latestRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [
        searchTerm,
        activeFilter,
        companyTypeFilter,
        contactTypeFilter,
        tierFilter,
        currentPage,
    ]);
    useEffect(() => {
        isMountedRef.current = true;
        fetchContacts();
        return () => {
            isMountedRef.current = false;
        };
    }, [fetchContacts]);
    const buildSearchParams = useCallback((opts) => {
        const params = {};
        if (opts.search)
            params.search = opts.search;
        if (opts.companyType)
            params.CompanyType = opts.companyType;
        if (opts.contactType)
            params.ContactType = opts.contactType;
        if (opts.tier)
            params.Tier = opts.tier;
        if (opts.otherFilter && opts.otherFilter !== "all")
            params.filter = opts.otherFilter;
        return params;
    }, []);
    const handleSearch = (e) => {
        if (e.key === "Enter") {
            setSearchParams(buildSearchParams({
                search: searchTerm || undefined,
                companyType: companyTypeFilter || undefined,
                contactType: contactTypeFilter || undefined,
                tier: tierFilter || undefined,
                otherFilter: activeFilter,
            }));
            setCurrentPage(1);
        }
    };
    const handleFilterChange = (filter) => {
        // For companyType, contactType, and tier, open the modal
        if (filter === "companyType" || filter === "contactType" || filter === "tier") {
            setFilterModalType(filter);
            setShowFilterMenu(false);
            return;
        }
        setActiveFilter(filter);
        setCurrentPage(1);
        setShowFilterMenu(false);
        // Clear specific filter values when changing filter mode
        setCompanyTypeFilter("");
        setContactTypeFilter("");
        setSearchParams(buildSearchParams({
            search: searchTerm || undefined,
            tier: tierFilter || undefined,
            otherFilter: filter,
        }));
    };
    const handleFilterApply = (value) => {
        if (!filterModalType)
            return;
        let newCompanyTypeFilter = companyTypeFilter;
        let newContactTypeFilter = contactTypeFilter;
        let newTierFilter = tierFilter;
        if (filterModalType === "companyType") {
            newCompanyTypeFilter = value;
            setCompanyTypeFilter(value);
        }
        else if (filterModalType === "contactType") {
            newContactTypeFilter = value;
            setContactTypeFilter(value);
        }
        else if (filterModalType === "tier") {
            newTierFilter = value;
            setTierFilter(value);
        }
        setActiveFilter(newCompanyTypeFilter || newContactTypeFilter
            ? newCompanyTypeFilter
                ? "companyType"
                : "contactType"
            : "all");
        setSearchParams(buildSearchParams({
            search: searchTerm || undefined,
            companyType: newCompanyTypeFilter || undefined,
            contactType: newContactTypeFilter || undefined,
            tier: newTierFilter || undefined,
        }));
        setCurrentPage(1);
    };
    const getActiveFilterCount = () => {
        let count = 0;
        if (companyTypeFilter)
            count++;
        if (contactTypeFilter)
            count++;
        if (tierFilter)
            count++;
        return count;
    };
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const handleTogglePortalAccess = async (contactId, access) => {
        try {
            await contactsService.updatePortalAccess({ contactId, access });
            // Check if component is still mounted before updating state
            if (!isMountedRef.current)
                return;
            // Update local state immediately for responsive UI
            setContacts((prev) => prev.map((contact) => contact.contactId === contactId
                ? {
                    ...contact,
                    access: { ...contact.access, portalAccess: access },
                }
                : contact));
        }
        catch (err) {
            // Ignore cancellation errors
            if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
                return;
            }
            if (!isMountedRef.current)
                return;
            console.error("Error updating portal access:", err);
            // Refresh to get the correct state
            fetchContacts();
        }
    };
    const handleEditClick = (contactId) => {
        const routePrefix = getRoutePrefix(user?.role || Role.ADMIN);
        navigate(`${routePrefix}/contacts/${contactId}/edit`);
    };
    const handleViewPortal = async (contactId) => {
        try {
            const response = await contactsService.switchAccount(contactId);
            // Check if component is still mounted before updating state
            if (!isMountedRef.current)
                return;
            const { token, user: clientUser } = response.data;
            // Update authentication context with the switched account
            switchToClientView(token, {
                id: clientUser.id,
                email: clientUser.email,
                name: clientUser.name || clientUser.email,
                role: clientUser.role,
            });
            // Defer navigation so auth state is committed before route render
            queueMicrotask(() => navigate(getHomeRoute(clientUser.role)));
        }
        catch (err) {
            // Ignore cancellation errors
            if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
                return;
            }
            if (!isMountedRef.current)
                return;
            console.error("Error switching account:", err);
            showError("Failed to switch to contact portal");
        }
    };
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsxs("div", { className: styles.header, children: [_jsx("div", { className: styles.titleSection, children: _jsx("h1", { className: styles.title, children: "Contacts" }) }), _jsxs("div", { className: styles.actions, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsx("span", { className: styles.searchIcon, children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }) }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search by name, email or company...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), onKeyDown: handleSearch, autoFocus: true })] }), _jsxs("div", { className: styles.filterDropdown, children: [_jsxs("button", { className: styles.filterBtn, onClick: () => setShowFilterMenu(!showFilterMenu), title: "Filter contacts", children: [_jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "currentColor", children: _jsx("path", { d: "M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" }) }), getActiveFilterCount() > 0 && (_jsx("span", { className: styles.filterBadge, children: getActiveFilterCount() }))] }), showFilterMenu && (_jsxs("div", { className: styles.filterMenu, children: [(companyTypeFilter || contactTypeFilter || tierFilter) && (_jsx("button", { onClick: () => {
                                                    setCompanyTypeFilter("");
                                                    setContactTypeFilter("");
                                                    setTierFilter("");
                                                    setActiveFilter("all");
                                                    setSearchParams(buildSearchParams({
                                                        search: searchTerm || undefined,
                                                    }));
                                                    setCurrentPage(1);
                                                    setShowFilterMenu(false);
                                                }, className: styles.filterOption, children: "Clear Filters" })), _jsxs("button", { onClick: () => handleFilterChange("companyType"), className: `${styles.filterOption} ${companyTypeFilter ? styles.activeFilter : ""}`, children: ["Filter by Company Type", companyTypeFilter && (_jsxs("span", { className: styles.filterValue, children: [": ", companyTypeFilter] }))] }), _jsxs("button", { onClick: () => handleFilterChange("contactType"), className: `${styles.filterOption} ${contactTypeFilter ? styles.activeFilter : ""}`, children: ["Filter by Contact Type", contactTypeFilter && (_jsxs("span", { className: styles.filterValue, children: [": ", contactTypeFilter] }))] }), _jsxs("button", { onClick: () => handleFilterChange("tier"), className: `${styles.filterOption} ${tierFilter ? styles.activeFilter : ""}`, children: ["Filter by Tier", tierFilter && (_jsxs("span", { className: styles.filterValue, children: [": ", tierFilter] }))] })] }))] })] })] }), loading && (_jsx("div", { className: styles.loading, children: _jsx("p", { children: "Loading contacts..." }) })), error && (_jsxs("div", { className: styles.error, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchContacts, className: styles.retryBtn, children: "Retry" })] })), !loading && !error && (_jsxs("div", { className: `table-page-listing`, children: [_jsx(ContactsTable, { contacts: contacts, onTogglePortalAccess: handleTogglePortalAccess, onEditClick: handleEditClick, onViewPortal: handleViewPortal }), _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, pageSize: pageSize, onPageChange: handlePageChange })] })), _jsx(ContactsFilterModal, { isOpen: filterModalType !== null, onClose: () => setFilterModalType(null), filterType: filterModalType || "companyType", currentValue: filterModalType === "companyType"
                    ? companyTypeFilter
                    : filterModalType === "contactType"
                        ? contactTypeFilter
                        : filterModalType === "tier"
                            ? tierFilter
                            : "", onApply: handleFilterApply })] }));
};
